import api from './api';

// Owner Dashboard API
export const ownerDashboardApi = {
  getDashboard(params = {}) {
    return api.get('/owner/dashboard', { params });
  }
};

// Owner Branch Management API
export const ownerBranchApi = {
  list(params = {}) {
    return api.get('/owner/branches', { params });
  },

  get(id) {
    return api.get(`/owner/branches/${id}`);
  },

  create(data) {
    return api.post('/owner/branches', data);
  },

  update(id, data) {
    return api.put(`/owner/branches/${id}`, data);
  },

  delete(id) {
    return api.delete(`/owner/branches/${id}`);
  },

  getStaff(branchId) {
    return api.get(`/owner/branches/${branchId}/staff`);
  }
};

// Dine-in Table QR Management API
// Each row is one physical table; its `code` is what the printed QR encodes.
export const ownerTableApi = {
  list(branchId, params = {}) {
    return api.get(`/owner/branches/${branchId}/tables`, { params });
  },

  create(branchId, data) {
    return api.post(`/owner/branches/${branchId}/tables`, data);
  },

  // Bulk-create a run of tables, e.g. { from: 1, to: 20 }. Existing numbers are skipped.
  generate(branchId, data) {
    return api.post(`/owner/branches/${branchId}/tables/generate`, data);
  },

  update(id, data) {
    return api.put(`/owner/tables/${id}`, data);
  },

  // Issues a new code — every sheet already printed for this table stops working.
  regenerateCode(id) {
    return api.post(`/owner/tables/${id}/regenerate-code`);
  },

  delete(id) {
    return api.delete(`/owner/tables/${id}`);
  }
};

// Owner Staff Management API
export const ownerStaffApi = {
  list(params = {}) {
    return api.get('/owner/staff', { params });
  },

  get(id) {
    return api.get(`/owner/staff/${id}`);
  },

  create(data) {
    return api.post('/owner/staff', data);
  },

  update(id, data) {
    return api.put(`/owner/staff/${id}`, data);
  },

  delete(id) {
    return api.delete(`/owner/staff/${id}`);
  },

  assign(staffId, outletIds) {
    return api.post(`/owner/staff/${staffId}/assign`, { outlet_ids: outletIds });
  }
};

// Owner Category Management API
export const ownerCategoryApi = {
  list(params = {}) {
    return api.get('/owner/categories', { params });
  },

  get(id) {
    return api.get(`/owner/categories/${id}`);
  },

  create(data) {
    return api.post('/owner/categories', data);
  },

  update(id, data) {
    return api.put(`/owner/categories/${id}`, data);
  },

  delete(id) {
    return api.delete(`/owner/categories/${id}`);
  },

  reorder(categories) {
    return api.post('/owner/categories/reorder', { categories });
  },

  uploadImage(formData) {
    return api.post('/owner/categories/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// Owner Product Management API (Shared Catalog)
export const ownerProductApi = {
  list(params = {}) {
    return api.get('/owner/products', { params });
  },

  get(id) {
    return api.get(`/owner/products/${id}`);
  },

  create(data) {
    return api.post('/owner/products', data);
  },

  update(id, data) {
    return api.put(`/owner/products/${id}`, data);
  },

  delete(id) {
    return api.delete(`/owner/products/${id}`);
  },

  assignToOutlets(productId, outlets) {
    return api.post(`/owner/products/${productId}/outlets`, { outlets });
  },

  updateOutletSettings(productId, outletId, settings) {
    return api.put(`/owner/products/${productId}/outlets/${outletId}`, settings);
  },

  uploadImage(formData) {
    return api.post('/owner/products/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// Owner Addon Management API (reusable across products, central stock toggle)
export const ownerAddonApi = {
  list(params = {}) {
    return api.get('/owner/addons', { params });
  },

  get(id) {
    return api.get(`/owner/addons/${id}`);
  },

  create(data) {
    return api.post('/owner/addons', data);
  },

  update(id, data) {
    return api.put(`/owner/addons/${id}`, data);
  },

  setAvailability(id, isAvailable) {
    return api.patch(`/owner/addons/${id}/availability`, { is_available: isAvailable });
  },

  // Replace the set of products this addon is attached to. Each entry is
  // { id, is_available } where is_available null means "inherit the central toggle".
  syncProducts(id, products) {
    return api.put(`/owner/addons/${id}/products`, { products });
  },

  // Turn an addon on/off for one product only. Pass null to clear the override
  // so the product goes back to following the central toggle.
  setProductAvailability(id, productId, isAvailable) {
    return api.patch(`/owner/addons/${id}/products/${productId}/availability`, {
      is_available: isAvailable,
    });
  },

  delete(id) {
    return api.delete(`/owner/addons/${id}`);
  }
};

// Owner Stock Management API
export const ownerStockApi = {
  // Get stock levels for a branch
  list(branchId, params = {}) {
    return api.get(`/owner/branches/${branchId}/stock`, { params });
  },

  // Add stock (purchase/initial)
  addStock(branchId, data) {
    return api.post(`/owner/branches/${branchId}/stock/add`, data);
  },

  // Remove stock (waste/expired)
  removeStock(branchId, data) {
    return api.post(`/owner/branches/${branchId}/stock/remove`, data);
  },

  // Adjust stock count
  adjustStock(branchId, data) {
    return api.post(`/owner/branches/${branchId}/stock/adjust`, data);
  },

  // Get movement history
  movements(branchId, params = {}) {
    return api.get(`/owner/branches/${branchId}/stock/movements`, { params });
  },

  // Get low stock alerts
  alerts(branchId) {
    return api.get(`/owner/branches/${branchId}/stock/alerts`);
  },

  // Update alert settings for a product
  updateAlertSettings(branchId, productId, settings) {
    return api.put(`/owner/branches/${branchId}/stock/products/${productId}/alerts`, settings);
  },

  // Get transfers for a branch
  branchTransfers(branchId, params = {}) {
    return api.get(`/owner/branches/${branchId}/stock/transfers`, { params });
  }
};

// Owner Stock Transfer API
export const ownerTransferApi = {
  list(params = {}) {
    return api.get('/owner/transfers', { params });
  },

  get(id) {
    return api.get(`/owner/transfers/${id}`);
  },

  create(data) {
    return api.post('/owner/transfers', data);
  },

  approve(id) {
    return api.post(`/owner/transfers/${id}/approve`);
  },

  ship(id, quantities = {}) {
    return api.post(`/owner/transfers/${id}/ship`, { quantities });
  },

  receive(id, quantities = {}) {
    return api.post(`/owner/transfers/${id}/receive`, { quantities });
  },

  cancel(id, reason) {
    return api.post(`/owner/transfers/${id}/cancel`, { reason });
  }
};

// Owner Customer Management API
// Note: Wallet management has been moved to Super Admin (platform-level)
export const ownerCustomerApi = {
  list(params = {}) {
    return api.get('/owner/customers', { params });
  },

  get(id) {
    return api.get(`/owner/customers/${id}`);
  },

  getStats() {
    return api.get('/owner/customers/stats');
  },

  getTiers() {
    return api.get('/owner/customers/tiers');
  },

  getOrders(customerId, params = {}) {
    return api.get(`/owner/customers/${customerId}/orders`, { params });
  },

  getActivity(customerId, params = {}) {
    return api.get(`/owner/customers/${customerId}/activity`, { params });
  }
};

// Owner Order Management API (Online Orders from Customer App)
export const ownerOrderApi = {
  list(params = {}) {
    return api.get('/owner/orders', { params });
  },

  get(id) {
    return api.get(`/owner/orders/${id}`);
  },

  getStats() {
    return api.get('/owner/orders/stats');
  },

  getNewCount() {
    return api.get('/owner/orders/new-count');
  },

  updateStatus(id, data) {
    return api.post(`/owner/orders/${id}/status`, data);
  },

  // Kitchen docket (prep ticket, no prices) — available from pending onwards.
  kitchenReceipt(id) {
    return api.get(`/owner/orders/${id}/kitchen-receipt`);
  },

  markKitchenPrinted(id) {
    return api.post(`/owner/orders/${id}/kitchen-printed`);
  },

  // Internal-rider assignment (Domino's-style)
  availableRiders(id) {
    return api.get(`/owner/orders/${id}/available-riders`);
  },

  assignRider(id, riderId) {
    return api.post(`/owner/orders/${id}/assign-rider`, { rider_id: riderId });
  },

  unassignRider(id, reason = null) {
    return api.post(`/owner/orders/${id}/unassign-rider`, { reason });
  }
};

// Owner Reviews API
export const ownerReviewApi = {
  list(params = {}) {
    return api.get('/owner/reviews', { params });
  },
  get(id) {
    return api.get(`/owner/reviews/${id}`);
  },
  stats() {
    return api.get('/owner/reviews/stats');
  },
  reply(id, reply) {
    return api.post(`/owner/reviews/${id}/reply`, { reply });
  },
  toggleVisibility(id) {
    return api.post(`/owner/reviews/${id}/toggle-visibility`);
  }
};

// Owner Notifications API
export const ownerNotificationApi = {
  list(params = {}) {
    return api.get('/owner/notifications', { params });
  },
  get(id) {
    return api.get(`/owner/notifications/${id}`);
  },
  preview(data) {
    return api.post('/owner/notifications/preview', data);
  },
  send(data) {
    return api.post('/owner/notifications', data);
  }
};

// Owner Reports API
export const ownerReportsApi = {
  sales(params = {}) {
    return api.get('/owner/reports/sales', { params });
  },
  orders(params = {}) {
    return api.get('/owner/reports/orders', { params });
  },
  products(params = {}) {
    return api.get('/owner/reports/products', { params });
  },
  customers(params = {}) {
    return api.get('/owner/reports/customers', { params });
  }
};

// Owner Settings API
export const ownerSettingsApi = {
  get() {
    return api.get('/owner/settings');
  },

  update(settings) {
    return api.put('/owner/settings', { settings });
  }
};

// Owner Loyalty Guide API (read-only view of platform loyalty rules)
export const ownerLoyaltyGuideApi = {
  get() {
    return api.get('/owner/loyalty/guide');
  }
};

// Loyverse POS Integration API
export const ownerLoyverseApi = {
  // Settings
  getSettings() {
    return api.get('/owner/loyverse/settings');
  },

  // Orders
  getOrders(params = {}) {
    return api.get('/owner/loyverse/orders', { params });
  },

  getOrder(id) {
    return api.get(`/owner/loyverse/orders/${id}`);
  },

  getNewOrdersCount() {
    return api.get('/owner/loyverse/orders/new-count');
  },

  // Status counts across the whole result set, not just the loaded page.
  getStats(params = {}) {
    return api.get('/owner/loyverse/orders/stats', { params });
  },

  getPrintData(id) {
    return api.get(`/owner/loyverse/orders/${id}/print`);
  },

  markAsPrinted(id) {
    return api.post(`/owner/loyverse/orders/${id}/printed`);
  },

  markAsCompleted(id) {
    return api.post(`/owner/loyverse/orders/${id}/completed`);
  },

  // Store Mappings
  getStoreMappings() {
    return api.get('/owner/loyverse/store-mappings');
  },

  saveStoreMapping(data) {
    return api.post('/owner/loyverse/store-mappings', data);
  },

  deleteStoreMapping(id) {
    return api.delete(`/owner/loyverse/store-mappings/${id}`);
  }
};

export default {
  dashboard: ownerDashboardApi,
  branches: ownerBranchApi,
  staff: ownerStaffApi,
  categories: ownerCategoryApi,
  products: ownerProductApi,
  addons: ownerAddonApi,
  stock: ownerStockApi,
  transfers: ownerTransferApi,
  customers: ownerCustomerApi,
  orders: ownerOrderApi,
  reports: ownerReportsApi,
  notifications: ownerNotificationApi,
  reviews: ownerReviewApi,
  settings: ownerSettingsApi,
  loyaltyGuide: ownerLoyaltyGuideApi,
  loyverse: ownerLoyverseApi
};
